import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { ChangeEvent, useEffect, useState } from 'react';

import Adornment from '@/operator/components/general/input/Adornment';
import {
    StyledAlignedEndInput,
    StyledInput,
} from '@/operator/components/modules/AuthPage/Styles';
import {
    doesWordContainsSpecialCharacters,
    removeWhiteSpacesAndLowerCaseAll,
} from '@/operator/utils/utils';
import { Button, CircularProgress, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useAppContext } from '@/operator/context/AppContext';
import { useOrgNavigate } from "app/routes/useOrgNavigate";
import { appRoutes } from '@/routes/constants';
import { useGetOrganizationLazyQuery } from 'generated/graphql';

const tenantURLDomain = process.env.NEXT_PUBLIC_TENANT_URL_DOMAIN;

const StyledWrapper = styled.div`
    width: 66%;
    @media (width <= 850px) {
        width: 93%;
        height: 50vh;
        padding-top: 34px;
        padding-bottom: 64px;
    }
`;

const regexForValidateVanityUrl = new RegExp(/^[a-zA-Z0-9 ]+$/);

export const Content = (): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { t } = useTranslation();
    const { operatorFrontendEndpointsMigration } = useFlags();

    const navigate = useOrgNavigate();
    const [businessName, setBusinessName] = useState('');
    const [vanityUrl, setVanityUrl] = useState('');
    const [debouncedInputValue, setDebouncedInputValue] = useState('');
    const [orgIdsBySubdomain, setOrgIdsBySubdomain] = useState<string[]>([]);
    const [apiError, setApiError] = useState<boolean>(false);

    const {
        setBusinessName: setBusinessNameContext,
        setVanityURL: setVanityURLContext,
    } = useAppContext();

    const [getOrganizationQuery, { loading }] = useGetOrganizationLazyQuery();

    // handle input function
    const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
        //@todo: this might be the cause of removing the first letter of the url
        setVanityUrl(removeWhiteSpacesAndLowerCaseAll(e.target.value));
    };

    // use debounce change state
    useEffect(() => {
        const delayInputTimeoutId = setTimeout(() => {
            setDebouncedInputValue(vanityUrl);
        }, 560);
        return () => clearTimeout(delayInputTimeoutId);
    }, [vanityUrl, 560]);

    // Fetch for results
    useEffect(() => {
        if (
            debouncedInputValue !== '' &&
            operatorFrontendEndpointsMigration !== undefined
        ) {
            getOrganizationQuery({
                variables: {
                    where: {
                        tenantUrl: debouncedInputValue,
                    },
                },
                onCompleted: ({ organization }) => {
                    if (organization?.id) {
                        setOrgIdsBySubdomain([debouncedInputValue]);
                    } else {
                        setOrgIdsBySubdomain([]);
                    }
                    setApiError(false);
                },
                onError: () => {
                    setApiError(true);
                },
            });
        }
    }, [debouncedInputValue, operatorFrontendEndpointsMigration]);

    useEffect(() => {
        setBusinessNameContext('');
        setVanityURLContext('');
    }, []);

    const onBlurBusinessNameInput = () => {
        if (!vanityUrl) {
            setVanityUrl(removeWhiteSpacesAndLowerCaseAll(businessName));
        }
    };

    const handleOnBuildClick = () => {
        setBusinessNameContext(businessName);
        setVanityURLContext(vanityUrl);
        navigate(appRoutes.AUTH_SETUP_PROGRESS);
    };

    const haveSubdomainsBeenLoaded = Boolean(orgIdsBySubdomain.length);

    const vanityURLMinimumLength = 3;
    const nameMaxLength = 50;
    const vanityUrlIsTooShort =
        vanityUrl && vanityUrl.length < vanityURLMinimumLength;

    const vanityUrlIsTooLong = vanityUrl && vanityUrl.length > nameMaxLength;

    const nameIsTooLong = businessName && businessName.length > nameMaxLength;

    const vanityURLContainsSpecialCharacters = vanityUrl
        ? !regexForValidateVanityUrl.test(vanityUrl)
        : false;

    const vanityUrlIsTaken = orgIdsBySubdomain.includes(vanityUrl) || vanityUrl.toLowerCase() === 'app';

    const isVanityURLValid =
        !vanityUrlIsTooShort &&
        !vanityURLContainsSpecialCharacters &&
        !vanityUrlIsTaken;

    const businessNameContainsSpecialCharacters =
        doesWordContainsSpecialCharacters(businessName);

    const disableContinueButton = () => {
        if (
            businessNameContainsSpecialCharacters ||
            !isVanityURLValid ||
            nameIsTooLong ||
            vanityUrlIsTooLong
        ) {
            return false;
        }

        const canProceed =
            // we have a vanity url
            vanityUrl &&
            // we have a businiess url
            businessName &&
            // did we receive something from API (subdomain already exists)
            // if empty, means no subdamin was found in the backend, can proceed
            !haveSubdomainsBeenLoaded &&
            !vanityUrlIsTaken &&
            // here we're using the error as signal of vanituURL availability
            !apiError &&
            !loading;

        return canProceed;
    };

    const renderErrorName = (): string => {
        if (nameIsTooLong) {
            return t('Auth.Setup.Build.BusinessNameInput.ErrorLabelTooLong');
        }
        if (businessNameContainsSpecialCharacters) {
            return t(
                'Auth.Setup.Build.BusinessNameInput.ErrorLabelSpecialCharacters',
            );
        }

        return '';
    };

    const renderErrorVanityInput = (): string => {
        if (vanityUrlIsTooShort) {
            return t('Auth.Setup.Build.UrlInput.ErrorLabelTooShort');
        }

        if (vanityURLContainsSpecialCharacters) {
            return t('Auth.Setup.Build.UrlInput.ErrorLabelSpecialCharacters');
        }

        if (vanityUrlIsTaken) {
            return t('Auth.Setup.Build.UrlInput.ErrorLabelIsTaken');
        }
        if (vanityUrlIsTooLong) {
            return t('Auth.Setup.Build.UrlInput.ErrorLabelTooLong');
        }

        return '';
    };

    return (
        <StyledWrapper>
            <StyledInput
                placeholder={t(
                    'Auth.Setup.Build.BusinessNameInput.PlaceHolder',
                )}
                value={businessName}
                label={t('Auth.Setup.Build.BusinessNameInput.Label')}
                helperText={t('Auth.Setup.Build.BusinessNameInput.SubLabel')}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setBusinessName(e.target.value);
                }}
                onBlur={onBlurBusinessNameInput}
                style={{ width: '100%', margin: '5px 5px 30px' }}
                id="org-name"
                required
                title={t('Auth.Setup.Build.BusinessNameInput.Title')}
                {...(businessNameContainsSpecialCharacters && {
                    errorBody: t(
                        'Auth.Setup.Build.BusinessNameInput.ErrorLabelSpecialCharacters',
                    ),
                })}
                {...((businessNameContainsSpecialCharacters ||
                    nameIsTooLong) && {
                    errorBody: renderErrorName(),
                })}
            />

            <StyledAlignedEndInput
                id="vanity-url"
                // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                onKeyDown={(e: any) => {
                    if (e.keyCode === 32) {
                        e.preventDefault();
                    }
                }}
                placeholder={
                    removeWhiteSpacesAndLowerCaseAll(businessName) ||
                    t('Auth.Setup.Build.UrlInput.PlaceHolder')
                }
                value={vanityUrl}
                label={t('Auth.Setup.Build.UrlInput.Label')}
                helperText={t('Auth.Setup.Build.UrlInput.SubLabel')}
                onChange={handleInput}
                style={{ width: '100%', margin: '5px 5px 30px' }}
                required
                title={t('Auth.Setup.Build.UrlInput.Title')}
                InputProps={{
                    endAdornment: (
                        <Container
                            sx={{
                                width: 'unset',
                                padding: '0 !important',
                                alignItems: 'center',
                                display: 'flex',
                            }}
                        >
                            {loading && (
                                <CircularProgress
                                    style={{
                                        marginLeft: '15px',
                                        width: '0.875rem',
                                        height: '0.875rem',
                                    }}
                                />
                            )}
                            <Adornment
                                position="end"
                                label={`.${tenantURLDomain}`}
                            />
                        </Container>
                    ),
                }}
                {...((!isVanityURLValid || vanityUrlIsTooLong) && {
                    error: true,
                    helperText: renderErrorVanityInput(),
                })}
            />
            <Button
                variant="contained"
                style={{ float: 'right' }}
                disabled={!disableContinueButton()}
                onClick={handleOnBuildClick}
            >
                Continue
            </Button>
        </StyledWrapper>
    );
};
